import { MESSAGE } from '@vindr/services/constants/message.constant';
import { messageError } from '@vindr/utils/formatText';
import { UseMutationResult } from '@tanstack/react-query';
import { FormInstance } from 'antd/lib/form';
import { MessageInstance } from 'antd/es/message/interface';
import { AxiosError } from 'axios';
import { DefaultErrorResponse } from '@vindr/services/interfaces';

interface HandleSaveParams<T, U, E extends DefaultErrorResponse> {
  message: MessageInstance;
  form?: FormInstance<T>;
  createParams?: (values: T) => T;
  editParams?: (values: T) => { id: string; data: T };
  deleteParams?: any;
  type: 'edit' | 'create' | 'delete';
  mutate: {
    create?: UseMutationResult<U, unknown, T, unknown>['mutate'];
    update?: UseMutationResult<U, unknown, { id: string; data: T }, unknown>['mutate'];
    delete?: UseMutationResult<any, unknown, T, unknown>['mutate'];
  };
  t?: (key: string) => string;
  onDone?: (val?: U) => void;
  onError?: (val?: E) => void;
  code?: {
    created?: keyof typeof MESSAGE.SUCCESS;
    updated?: keyof typeof MESSAGE.SUCCESS;
    deleted?: keyof typeof MESSAGE.SUCCESS;
  };
}

const handleSave = <T, U, E extends DefaultErrorResponse>(props: HandleSaveParams<T, U, E>) => {
  const {
    message,
    form,
    createParams,
    editParams,
    deleteParams,
    type,
    mutate,
    t,
    onDone,
    onError: callbackError,
    code: { created, updated, deleted },
  } = props;

  const mutateCreate = mutate.create;
  const mutateUpdate = mutate.update;
  const mutateDelete = mutate.delete;

  const onError = (error: AxiosError<E>) => {
    const msg = messageError(error);

    if (callbackError) {
      callbackError(error?.response?.data);
    } else {
      message.error(t ? t(msg) : msg);
    }
  };

  const onSuccess = (messageCode: keyof typeof MESSAGE.SUCCESS) => {
    if (!messageCode) return;
    const msg = MESSAGE.SUCCESS[messageCode];

    message.success(t ? t(msg) : msg);
    form?.resetFields();
  };

  switch (type) {
    case 'create':
      form.validateFields().then(async (values) => {
        mutateCreate(createParams?.(values) || values, {
          onSuccess: (val) => {
            onSuccess(created);
            onDone?.(val);
          },
          onError,
        });
      });
      break;

    case 'edit': {
      form.validateFields().then(async (values) => {
        console.log('🚀 ~ form.validateFields ~ values:', values)
        mutateUpdate(editParams(values), {
          onSuccess: (val) => {
            onSuccess(updated);
            onDone?.(val);
          },
          onError,
        });
      });
      break;
    }

    case 'delete':
      mutateDelete(deleteParams, {
        onSuccess: (val) => {
          onSuccess(deleted);
          onDone?.(val);
        },
        onError,
      });
      break;
    default:
      throw new Error('Invalid type');
  }
};

export default handleSave;
